import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000ApiValueToDisplayYmd, u2000TodayOrgYmd } from '../u2000Core/tools/U2000DateTimeHelpers';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

/** Jour civil `yyyy-MM-dd` depuis une valeur API (préfixe civil prioritaire, jamais décalage local). */
function toCivilHolidayYmd(value: unknown): string {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  const prefix = raw.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(prefix)) {
    return prefix;
  }
  return U2000ApiValueToDisplayYmd(raw) ?? '';
}

const LOGICALUNIT_URL = 'U3303';

const LogicalUnitEndpoints = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string) => `${LOGICALUNIT_URL}/${id}/audits`,
};

export interface U3303InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
}

export interface U3303HolidayYearSearchRow {
  id: string;
  year: number;
  dayCount: number;
  version: number;
}

export interface U3303HolidayDayApi {
  id: string;
  holidayDate: string;
  label: string;
  version?: number;
}

export interface U3303HolidayYearGet {
  id: string;
  year: number;
  version: number;
  days: U3303HolidayDayApi[];
}

export interface U3303HolidayDayFormRow {
  id: string;
  holidayDate: string;
  label: string;
  version?: number;
}

export interface U3303HolidayYearFormRow {
  id: string;
  year: number;
  version: number;
  days: U3303HolidayDayFormRow[];
}

export interface U3303HolidayYearCreateBody {
  year: number;
  days: Array<{ id: string; holidayDate: string; label: string }>;
}

export interface U3303HolidayYearUpdateBody {
  id: string;
  version: number;
  days: Array<{ id: string; holidayDate: string; label: string }>;
}

export interface U3303HolidayYearDeleteBody {
  id: string;
  version: number;
}

function newClientUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `00000000-0000-4000-8000-${Math.random().toString(16).slice(2, 14).padEnd(12, '0')}`;
}

export function createEmptyHolidayDayRow(): U3303HolidayDayFormRow {
  return {
    id: newClientUuid(),
    holidayDate: '',
    label: '',
    version: 0,
  };
}

export function createEmptyU3303FormRow(): U3303HolidayYearFormRow {
  const todayYmd = u2000TodayOrgYmd();
  const year = Number(todayYmd.slice(0, 4));
  return {
    id: '',
    year: Number.isInteger(year) ? year : 0,
    version: 0,
    days: [],
  };
}

export function normalizeU3303FromApi(row: U3303HolidayYearGet): U3303HolidayYearFormRow {
  return {
    id: row.id,
    year: row.year,
    version: row.version,
    days: (row.days ?? []).map((d) => ({
      id: d.id,
      holidayDate: toCivilHolidayYmd(d.holidayDate),
      label: d.label ?? '',
      version: d.version ?? 0,
    })),
  };
}

export function formToSearchRow(data: U3303HolidayYearFormRow): U3303HolidayYearSearchRow {
  return {
    id: data.id,
    year: data.year,
    dayCount: data.days?.length ?? 0,
    version: data.version,
  };
}

function mapDaysForPayload(days: U3303HolidayDayFormRow[]): Array<{ id: string; holidayDate: string; label: string }> {
  return (days ?? []).map((d) => ({
    id: d.id,
    holidayDate: toCivilHolidayYmd(d.holidayDate),
    label: String(d.label ?? '').trim(),
  }));
}

export function buildCreateBody(data: U3303HolidayYearFormRow): U3303HolidayYearCreateBody {
  return {
    year: Number(data.year),
    days: mapDaysForPayload(data.days),
  };
}

export function buildUpdateBody(data: U3303HolidayYearFormRow): U3303HolidayYearUpdateBody {
  return {
    id: data.id,
    version: data.version,
    days: mapDaysForPayload(data.days),
  };
}

export function buildDeleteBody(data: Pick<U3303HolidayYearFormRow, 'id' | 'version'>): U3303HolidayYearDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

/** Codes d’erreur i18n locaux (sans préfixe) ou undefined si OK. */
export function validateU3303FormRow(data: U3303HolidayYearFormRow): Record<string, string> {
  const result: Record<string, string> = {};
  const year = Number(data.year);
  if (!Number.isInteger(year) || year < 1900 || year > 2100) {
    result.year = 'errYearInvalid';
  }

  const seenDates = new Set<string>();
  (data.days ?? []).forEach((day, index) => {
    const date = String(day.holidayDate ?? '').trim();
    const label = String(day.label ?? '').trim();
    if (!date) {
      result[`days.${index}.holidayDate`] = 'errHolidayDateRequired';
    } else if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      result[`days.${index}.holidayDate`] = 'errHolidayDateRequired';
    } else if (Number.isInteger(year) && Number(date.slice(0, 4)) !== year) {
      result[`days.${index}.holidayDate`] = 'errHolidayDateYearMismatch';
    } else if (seenDates.has(date)) {
      result[`days.${index}.holidayDate`] = 'errHolidayDateDuplicate';
    } else {
      seenDates.add(date);
    }
    if (!label) {
      result[`days.${index}.label`] = 'errLabelRequired';
    }
  });

  return result;
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearchHolidayYears = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetHolidayYear = (axios: AxiosInstance, id: string) => {
  return axios.get(LogicalUnitEndpoints.GET(id));
};

export const doCreateHolidayYear = (axios: AxiosInstance, body: U3303HolidayYearCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateHolidayYear = (axios: AxiosInstance, id: string, body: U3303HolidayYearUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteHolidayYear = (axios: AxiosInstance, body: U3303HolidayYearDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(body.id), body);
};

export const doGetHolidayYearAudits = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(String(id)), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: { errorCode?: string }, errorResult: U2000ApiErrorResult): void {
  if (err.errorCode === 'BU330301') {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: nameSpace }),
      text: t('errorBU330301', { ns: nameSpace }),
    });
  }
  if (err.errorCode === 'BU330302') {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: nameSpace }),
      text: t('errorBU330302', { ns: nameSpace }),
    });
  }
  if (err.errorCode === 'BU330303') {
    errorResult.genericErrors.push({
      title: t('errorTitle', { ns: nameSpace }),
      text: t('errorBU330303', { ns: nameSpace }),
    });
  }
}
